import React, { useState, useEffect } from 'react';
import { 
  Home, FileText, TrendingUp, AlertTriangle, Calendar, Package, PieChart, 
  Menu, X, Settings, Pill, ChevronDown, ChevronRight,
  DollarSign, Boxes, ShieldAlert, Snowflake, CheckCircle, Box, TrendingUp as TopIcon,
  History
} from 'lucide-react';

// Sub-items para el menú de Farmacia
const farmaciaSubItems = [
  { id: 'farmacia-valor', label: 'Valor Total', icon: DollarSign, color: 'emerald', filter: 'all' },
  { id: 'farmacia-unidades', label: 'Total Unidades', icon: Boxes, color: 'blue', filter: 'all' },
  { id: 'farmacia-controlados', label: 'Controlados', icon: ShieldAlert, color: 'red', filter: 'controlados' },
  { id: 'farmacia-refrigerados', label: 'Refrigerados', icon: Snowflake, color: 'cyan', filter: 'refrigerados' },
  { id: 'farmacia-vigentes', label: 'Vigentes', icon: CheckCircle, color: 'green', filter: 'vigentes' },
  { id: 'farmacia-ambiente', label: 'Ambiente', icon: Box, color: 'slate', filter: 'ambiente' },
  { id: 'farmacia-top3', label: 'Top 3 Valiosos', icon: TopIcon, color: 'purple', filter: 'top3' },
];

const getMenuItems = (userRole, hideReposiciones = false) => {
  // Si es admin con hideReposiciones (admin de farmacia), mostrar ambas opciones
  if ((userRole === 'admin' || userRole === 'administrador') && hideReposiciones) {
    return [
      { id: 'admin', label: 'Administración', icon: Settings, color: 'emerald' },
      { id: 'historial-consigna', label: 'Historial Consigna', icon: History, color: 'purple' },
      { id: 'farmacia', label: 'Inventario Farmacia', icon: Pill, color: 'cyan', hasSubItems: true, subItems: farmaciaSubItems },
    ];
  }
  
  // Si es admin normal, solo mostrar la opción de Administración
  if (userRole === 'admin' || userRole === 'administrador') {
    return [
      {
        id: 'admin',
        label: 'Administración',
        icon: Settings,
        color: 'emerald'
      },
      {
        id: 'historial-consigna',
        label: 'Historial Consigna',
        icon: History,
        color: 'purple'
      }
    ];
  }
  
  // Para usuarios de farmacia (hideReposiciones = true), mostrar dashboard de farmacia
  if (hideReposiciones) {
    return [
      { id: 'farmacia', label: 'Inventario Farmacia', icon: Pill, color: 'cyan', hasSubItems: true, subItems: farmaciaSubItems },
      { id: 'dashboard', label: 'Dashboard General', icon: Home, color: 'blue' },
      { id: 'expiry-risk', label: 'Riesgo Vencimiento', icon: Calendar, color: 'yellow' },
      { id: 'reports', label: 'Reportes', icon: FileText, color: 'slate' },
    ];
  }
  
  // Para usuarios normales, mostrar todas las opciones
  const baseItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, color: 'blue' },
    { id: 'reports', label: 'Reportes', icon: FileText, color: 'slate' },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, color: 'blue' },
    { id: 'critical', label: 'Items Críticos', icon: AlertTriangle, color: 'red' },
    { id: 'expiry-risk', label: 'Riesgo Vencimiento', icon: Calendar, color: 'yellow' },
    { id: 'reposiciones', label: 'Reposiciones', icon: Package, color: 'cyan' },
    { id: 'charts', label: 'Business Intelligence', icon: PieChart, color: 'purple' },
  ];
  
  return baseItems;
};

const getColorClasses = (color, isActive) => {
  const colors = {
    blue: isActive ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50',
    slate: isActive ? 'bg-slate-600 text-white' : 'text-slate-600 hover:bg-slate-50',
    red: isActive ? 'bg-red-600 text-white' : 'text-red-600 hover:bg-red-50',
    yellow: isActive ? 'bg-yellow-600 text-white' : 'text-yellow-600 hover:bg-yellow-50',
    cyan: isActive ? 'bg-cyan-600 text-white' : 'text-cyan-600 hover:bg-cyan-50',
    purple: isActive ? 'bg-purple-600 text-white' : 'text-purple-600 hover:bg-purple-50',
    emerald: isActive ? 'bg-emerald-600 text-white' : 'text-emerald-600 hover:bg-emerald-50',
    green: isActive ? 'bg-green-600 text-white' : 'text-green-600 hover:bg-green-50',
  };
  return colors[color] || colors.blue;
};

const getSubItemColorClasses = (color, isActive) => {
  const colors = {
    blue: isActive ? 'bg-blue-100 text-blue-700 border-l-blue-500' : 'text-blue-600 hover:bg-blue-50 border-l-transparent',
    slate: isActive ? 'bg-slate-100 text-slate-700 border-l-slate-500' : 'text-slate-600 hover:bg-slate-50 border-l-transparent',
    red: isActive ? 'bg-red-100 text-red-700 border-l-red-500' : 'text-red-600 hover:bg-red-50 border-l-transparent',
    yellow: isActive ? 'bg-yellow-100 text-yellow-700 border-l-yellow-500' : 'text-yellow-600 hover:bg-yellow-50 border-l-transparent',
    cyan: isActive ? 'bg-cyan-100 text-cyan-700 border-l-cyan-500' : 'text-cyan-600 hover:bg-cyan-50 border-l-transparent',
    purple: isActive ? 'bg-purple-100 text-purple-700 border-l-purple-500' : 'text-purple-600 hover:bg-purple-50 border-l-transparent',
    emerald: isActive ? 'bg-emerald-100 text-emerald-700 border-l-emerald-500' : 'text-emerald-600 hover:bg-emerald-50 border-l-transparent',
    green: isActive ? 'bg-green-100 text-green-700 border-l-green-500' : 'text-green-600 hover:bg-green-50 border-l-transparent',
  };
  return colors[color] || colors.blue;
};

export const Sidebar = ({ 
  isOpen, 
  setIsOpen, 
  activeTab, 
  setActiveTab, 
  isMobile, 
  userRole, 
  hideReposiciones = false,
  onFarmaciaFilterChange // Nueva prop para manejar filtros de farmacia
}) => {
  const menuItems = getMenuItems(userRole, hideReposiciones);
  const [expandedMenus, setExpandedMenus] = useState({ farmacia: true }); // Farmacia expandido por defecto
  const [activeFarmaciaFilter, setActiveFarmaciaFilter] = useState(null);
  
  // Expandir automáticamente cuando el tab activo es farmacia
  useEffect(() => {
    if (activeTab === 'farmacia') {
      setExpandedMenus(prev => ({ ...prev, farmacia: true }));
    }
  }, [activeTab]);
  
  const toggleExpand = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleSubItemClick = (subItem) => {
    setActiveTab('farmacia');
    setActiveFarmaciaFilter(subItem.filter);
    if (onFarmaciaFilterChange) {
      onFarmaciaFilterChange(subItem.filter);
    }
    if (isMobile) setIsOpen(false);
  };
  
  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isOpen ? 'w-64' : 'lg:w-20'}
          bg-white shadow-xl
          transition-all duration-300 ease-in-out
          flex flex-col
        `}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          {isOpen && (
            <div className="flex items-center gap-3 flex-1">
              <img 
                src="/mdlink-logo.png" 
                alt="MD Link" 
                className="h-12 w-auto max-w-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg items-center justify-center hidden"
                style={{display: 'none'}}
              >
                <span className="text-white font-bold text-lg">MD</span>
              </div>
            </div>
          )}
          {!isOpen && (
            <div className="flex items-center justify-center w-full py-2">
              <img 
                src="/mdlink-logo.png" 
                alt="MD Link" 
                className="h-10 w-auto max-w-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg items-center justify-center hidden"
                style={{display: 'none'}}
              >
                <span className="text-white font-bold text-sm">MD</span>
              </div>
            </div>
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              const isExpanded = expandedMenus[item.id];
              const hasSubItems = item.hasSubItems && item.subItems;
              
              return (
                <li key={item.id}>
                  {/* Main Menu Item */}
                  <button
                    onClick={() => {
                      if (hasSubItems && isOpen) {
                        toggleExpand(item.id);
                        setActiveTab(item.id);
                        setActiveFarmaciaFilter(null);
                        if (onFarmaciaFilterChange) onFarmaciaFilterChange(null);
                      } else {
                        setActiveTab(item.id);
                        if (isMobile) setIsOpen(false);
                      }
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-3 rounded-lg
                      transition-all duration-200
                      ${getColorClasses(item.color, isActive && !activeFarmaciaFilter)}
                      ${isActive && !activeFarmaciaFilter ? 'shadow-md' : ''}
                    `}
                    title={!isOpen ? item.label : ''}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${!isOpen ? 'mx-auto' : ''}`} />
                    {isOpen && (
                      <>
                        <span className="font-medium text-sm truncate flex-1 text-left">{item.label}</span>
                        {hasSubItems && (
                          isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                        )}
                      </>
                    )}
                  </button>
                  
                  {/* Sub Items (only when sidebar is open) */}
                  {hasSubItems && isOpen && isExpanded && (
                    <ul className="mt-1 ml-2 space-y-1">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = activeTab === 'farmacia' && activeFarmaciaFilter === subItem.filter;
                        
                        return (
                          <li key={subItem.id}>
                            <button
                              onClick={() => handleSubItemClick(subItem)}
                              className={`
                                w-full flex items-center gap-2 px-3 py-2 rounded-md
                                transition-all duration-200 border-l-2
                                ${getSubItemColorClasses(subItem.color, isSubActive)}
                                text-sm
                              `}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{subItem.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        {isOpen && (
          <div className="p-4 border-t border-slate-200">
            <div className="text-xs text-slate-500 text-center">
              MD Link Dashboard v2.1
            </div>
          </div>
        )}
      </aside>
    </>
  );
};
